/* eslint-disable react-hooks/exhaustive-deps */
import { TrackPoint, useEventContext } from 'providers/EventProvider';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Stack, Paper, PaperProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSplitContext } from 'providers/SplitProvider';
import { getBearing } from 'utils/splitHelpers';

const PINK = '#ff0060';
const SELECTED_BG = '#ffc1e0';

interface SplitItemProps extends PaperProps {
  selected?: boolean;
}

const SplitItem = styled((props: SplitItemProps) => <Paper {...props} />)<SplitItemProps>(({ selected }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  borderBottom: '1px solid #ccc',
  borderRight: '1px solid #ccc',
  background: selected ? SELECTED_BG : '#fff',
  fontWeight: selected ? 'bold' : 'normal',
  color: '#222',
  padding: '2px 5px',
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  touchAction: 'none',
  '@media (pointer: coarse)': {
    minHeight: 40,
    padding: '8px 5px'
  },
  '&:nth-of-type(3n)': {
    borderRight: 'none'
  }
}));

const sumArrayElements = (array: number[]): number => {
  return array.reduce((a, b) => a + b, 0);
};

const calculatePlayerColor = (time: number, best: number): string => {
  const diff = time - best;
  const max = best * 0.5;
  let ff = Math.floor((510 * diff) / max);
  if (ff > 510) {
    ff = 510;
  }
  let red = ff <= 255 ? ff.toString(16).toUpperCase() : 'FF';
  if (red.length === 1) {
    red = '0' + red;
  }
  let green = ff <= 255 ? 'FF' : (510 - ff).toString(16).toUpperCase();
  if (green.length === 1) {
    green = '0' + green;
  }
  return `#${red}${green}00`;
};

const getSplitLabel = (index: number, total: number) => {
  if (index === 0 && index === total - 1) return 'S-F';
  if (index === 0) return `S-${index + 1}`;
  if (index === total - 1) return `${index}-F`;
  return `${index}-${index + 1}`;
};

const isIndexInRange = (index: number, range: [number, number]) => {
  const [start, end] = range;
  const min = Math.min(start, end);
  const max = Math.max(start, end);
  return index >= min && index <= max;
};

const rangeToSplitIndices = (range: [number, number]) => {
  const [start, end] = range;
  const min = Math.min(start, end);
  const max = Math.max(start, end);
  const indices: number[] = [];
  for (let i = min; i <= max; i++) indices.push(i);
  return indices;
};

const Splits = () => {
  const { event, showControls, setShowControls, players, updateVisiblePlayers } = useEventContext();
  const {
    setControls,
    splits,
    options,
    activeSplits,
    setActiveSplits,
    setTime,
    setMaxTime,
    controls,
    setMapCenter,
    setMapBearing,
    setMapTransitionMs,
    setMapFitPoints
  } = useSplitContext();

  const [isDragging, setIsDragging] = useState(false);
  const [dragRange, setDragRange] = useState<[number, number] | null>(null);
  const isDraggingRef = useRef(false);
  const dragRangeRef = useRef<[number, number] | null>(null);

  const isSplitSelected = (index: number) => {
    if (isDragging && dragRange) {
      return isIndexInRange(index, dragRange);
    }
    return activeSplits.includes(index);
  };

  const getSplitIndexFromPoint = (clientX: number, clientY: number) => {
    const el = document.elementFromPoint(clientX, clientY);
    const item = el?.closest('[data-split-index]');
    if (!item) return null;
    const index = Number(item.getAttribute('data-split-index'));
    return Number.isInteger(index) ? index : null;
  };

  const beginDrag = (index: number, pointerId: number, target: HTMLElement) => {
    target.setPointerCapture(pointerId);
    const range: [number, number] = [index, index];
    isDraggingRef.current = true;
    dragRangeRef.current = range;
    setIsDragging(true);
    setDragRange(range);
  };

  const updateDragFromPoint = (clientX: number, clientY: number) => {
    if (!isDraggingRef.current || !dragRangeRef.current) return;
    const index = getSplitIndexFromPoint(clientX, clientY);
    if (index === null || dragRangeRef.current[1] === index) return;
    const range: [number, number] = [dragRangeRef.current[0], index];
    dragRangeRef.current = range;
    setDragRange(range);
  };

  const endDrag = () => {
    if (!isDraggingRef.current) return;
    if (dragRangeRef.current) {
      setActiveSplits(rangeToSplitIndices(dragRangeRef.current));
    }
    isDraggingRef.current = false;
    dragRangeRef.current = null;
    setIsDragging(false);
    setDragRange(null);
  };

  const onPointerDown = (index: number) => {
    return (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      beginDrag(index, e.pointerId, e.currentTarget);
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    updateDragFromPoint(e.clientX, e.clientY);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    e.preventDefault();
    endDrag();
  };

  const handleReset = () => {
    setControls([...event.controlsAnalytic]);
  };

  const handleResetNorth = () => {
    setMapTransitionMs(0);
    setMapBearing(0);
  };

  React.useEffect(() => {
    changeSplits(activeSplits);
  }, [activeSplits, players]);

  const changeSplits = (selected: number[]) => {
    const _players = players
      .filter((player) => player.options?.show)
      .map((player) => {
        if (player.splits) {
          player.display.track = selected.reduce<TrackPoint[]>((acc, i) => {
            const split = player.splits?.[i];
            if (split) {
              acc.push(...split.track);
            }
            return acc;
          }, []);
        }
        if (selected.length > 0) {
          player.distance = sumArrayElements(selected.map((i) => player.splits[i].distance));
          player.time = sumArrayElements(selected.map((i) => player.splits[i].time));
        }
        return player;
      })
      .sort((a, b) => a.time - b.time)
      .filter((a) => a.time > 0);

    let max = 0;
    if (_players.length > 0) {
      const best = _players[0].time;
      max = _players[_players.length - 1].time;
      _players.forEach((player) => {
        player.diff = player.time - best;
        player.display.color = options.timeGradient ? calculatePlayerColor(player.time, best) : player.color;
      });
    }
    updateVisiblePlayers([..._players]);
    setTime(0);
    setMaxTime(max * 1000);

    setMapTransitionMs(0);
    if (selected.length > 0) {
      const first = selected[0];
      const last = selected[selected.length - 1];
      const startControl = controls[first];
      const nextControl = controls[first + 1];
      const endControl = controls[last + 1];
      if (startControl && endControl) {
        setMapCenter({
          lat: (startControl.position.lat + endControl.position.lat) / 2,
          lng: (startControl.position.lng + endControl.position.lng) / 2
        });
        const heading = getBearing(startControl.position, nextControl.position);
        setMapBearing((360 - heading) % 360);
        setMapFitPoints([
          {lat: startControl.position.lat, lng: startControl.position.lng},
          {lat: endControl.position.lat, lng: endControl.position.lng}
        ]);
      }
    } else {
      setMapBearing(0);
      setMapFitPoints(null);
    }
  };

  useEffect(() => {
    updateVisiblePlayers((players) => {
      if (players.length > 0) {
        const best = players[0].time;
        players.forEach((player) => {
          player.display.color = options.timeGradient ? calculatePlayerColor(player.time, best) : player.color;
        });
      }
      return [...players];
    });
  }, [options.opacity, options.timeGradient]);

  return (
    <Box>
      <Box
        sx={{
          borderBottom: '1px solid rgba(0,0,0,0.4)',
          px: 2,
          py: 1,
          color: PINK,
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="subtitle1" sx={{ color: PINK, fontWeight: 'bold' }}>
          Splits
        </Typography>
        <Box>
          <Button variant="text" size="small" disableRipple onClick={handleResetNorth}>
            North
          </Button>
          <Button variant="text" size="small" disableRipple onClick={handleReset}>
            Reset
          </Button>
          <Button variant="text" size="small" disableRipple onClick={() => setShowControls(!showControls)}>
            Show
          </Button>
        </Box>
      </Box>
      <Stack
        spacing={0}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          touchAction: 'none',
          userSelect: 'none'
        }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {splits.map((_, i) => {
          const selected = isSplitSelected(i);

          return (
            <SplitItem
              key={i}
              data-split-index={i}
              selected={selected}
              onPointerDown={onPointerDown(i)}
            >
              <Typography variant="body2" sx={{ width: '100%', fontWeight: selected ? 'bold' : 'normal' }}>
                {getSplitLabel(i, splits.length)}
              </Typography>
            </SplitItem>
          );
        })}
      </Stack>
    </Box>
  );
};

export default Splits;
